import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormHelpText, Image, Banner, PaginationControls } from '@spotify-internal/encore-web';
import slice from 'lodash/slice';

import withTranslation from 'lib/shared/i18n/withTranslation';

import styles from './ImageChooser.module.scss';

const IMAGES_PER_PAGE = 4;

// We're using the PureComponent here instead of BaseField since BaseField relies
// on the value of the field being a string and for simplicity/consistency we are
// using a Set here for the selected images, which are the values that we put back
// into the formData via the onFieldChange function.
//
//
// Originally this component did extend BaseField, but we tracked down a bug
// where the associatedContent field in formData got cleared when moving back
// and forth in the form due to the this.updateFieldData() call in the
// constructor for BaseField and the only way to fix it was to pass a
// value prop, but we can't do that since BaseField only supports string.
class ImageChooser extends PureComponent {
  static propTypes = {
    t: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    field: PropTypes.object.isRequired,
    errorMessageKey: PropTypes.string.isRequired,
    forceValidation: PropTypes.bool.isRequired,
    valid: PropTypes.bool.isRequired,
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedImages: PropTypes.arrayOf(PropTypes.string).isRequired,
    onFieldChange: PropTypes.func.isRequired,
    paginationOfKey: PropTypes.string.isRequired,
    disclaimer: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    const { selectedImages, images, onFieldChange, field } = props;

    const relevantSelectedImages = new Set(selectedImages.filter(image => images.includes(image)));

    this.state = {
      selectedImages: relevantSelectedImages,
      firstImageIndex: 0,
      lastImageIndex: IMAGES_PER_PAGE,
    };

    // This mimics the behaviour of the BaseField where the onFieldChange gets called
    // in the constructor to make sure the values get propagated at first render.
    // We need this to ensure that as you navigate forwards and back in the form,
    // we don't lose the state of selected images.
    if (relevantSelectedImages.size > 0) {
      onFieldChange({
        fieldIdentifier: field.name,
        value: relevantSelectedImages,
        valid: true,
      });
    }
  }

  toggleImage(onFieldChange, field, value) {
    const { selectedImages } = this.state;

    const newSelectedImages = new Set(selectedImages);
    const imageUnselected = newSelectedImages.delete(value);
    if (!imageUnselected) {
      newSelectedImages.add(value);
    }
    this.setState({ selectedImages: newSelectedImages });

    const valid = field.required ? newSelectedImages.size > 0 : true;

    onFieldChange({
      fieldIdentifier: field.name,
      value: newSelectedImages,
      valid: valid,
    });
  }

  isSelectedImage(image) {
    const { selectedImages } = this.state;
    return selectedImages.has(image);
  }

  getError() {
    const { forceValidation, valid, t, errorMessageKey } = this.props;
    const { changed } = this.state;

    const showError = (forceValidation || changed) && !valid;

    if (!showError) {
      return null;
    }

    return (
      <FormHelpText data-testid="validation-error" error>
        {t(errorMessageKey)}
      </FormHelpText>
    );
  }

  computeImagesToDisplay() {
    const { images } = this.props;
    const { firstImageIndex, lastImageIndex } = this.state;
    return slice(images, firstImageIndex, lastImageIndex);
  }

  renderPaginationControls() {
    const { images, t, paginationOfKey } = this.props;
    const { firstImageIndex, lastImageIndex } = this.state;

    const paginationRequired = images.length > IMAGES_PER_PAGE;
    if (!paginationRequired) {
      return null;
    }

    const onIncrement = () =>
      this.setState({
        firstImageIndex: firstImageIndex + IMAGES_PER_PAGE,
        lastImageIndex: lastImageIndex + IMAGES_PER_PAGE,
      });

    const onDecrement = () =>
      this.setState({
        firstImageIndex: firstImageIndex - IMAGES_PER_PAGE,
        lastImageIndex: lastImageIndex - IMAGES_PER_PAGE,
      });

    return (
      <div className={styles.paginationContainer} data-testid="pagination">
        <PaginationControls
          onIncrement={lastImageIndex < images.length ? () => onIncrement() : undefined}
          onDecrement={firstImageIndex > 1 ? () => onDecrement() : undefined}
        >
          {firstImageIndex + 1}-{Math.min(lastImageIndex, images.length)} {t(paginationOfKey)} {images.length}
        </PaginationControls>
      </div>
    );
  }

  render() {
    const { t, label, disclaimer, onFieldChange, field } = this.props;
    const imagesToDisplay = this.computeImagesToDisplay();
    return (
      <>
        <p>
          {t(label)}
          {field.required && <span className={styles.required}>*</span>}
        </p>
        <Banner colorSet="announcement">{t(disclaimer)}</Banner>
        {this.getError()}
        <ul className={styles.images} role="listbox" aria-multiselectable="true">
          {imagesToDisplay.map((image, index) => (
            <li
              role="option"
              aria-selected={this.isSelectedImage(image)}
              tabIndex={index}
              className={this.isSelectedImage(image) ? `${styles.image} ${styles.selected}` : `${styles.image}`}
              key={index}
              onClick={() => this.toggleImage(onFieldChange, field, image)}
              onKeyDown={() => this.toggleImage(onFieldChange, field, image)}
            >
              <Image src={image} alt={t(label)} imageHeight="150px" imageWidth="150px" data-testid={image} crop />
            </li>
          ))}
        </ul>
        {this.renderPaginationControls()}
      </>
    );
  }
}

export default withTranslation()(ImageChooser);
